import Layout from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { APP_NAME, SUPPORT_EMAIL, PRIVACY_EMAIL } from "@/lib/constants";
import { SEO } from "@/components/SEO";

const Terms = () => {
  return (
    <Layout>
      <SEO 
        title="תנאי שימוש" 
        description={`תנאי השימוש הרשמיים בפלטפורמת ${APP_NAME}.`} 
      />
      <div className="bg-white min-h-screen" dir="rtl">
        <div className="border-b border-slate-100 pt-32 pb-10">
          <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
            <Link to="/" className="inline-flex items-center text-sm text-slate-400 hover:text-slate-900 mb-8 font-medium transition-colors">
              <ArrowRight className="h-4 w-4 ms-2" />
              חזרה לדף הבית
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">תנאי שימוש</h1>
            <p className="text-slate-500 mt-3">עדכון אחרון: פברואר 2026</p>
          </div>
        </div>

        <div className="container mx-auto px-6 lg:px-12 max-w-3xl py-12">
          <div className="prose prose-slate max-w-none prose-headings:tracking-tight prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-lg prose-p:leading-relaxed prose-p:text-slate-600 prose-li:text-slate-600 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline">

            <p className="text-lg text-slate-700 font-medium">
              ברוכים הבאים ל-{APP_NAME}. תנאי שימוש אלו מגדירים את הכללים וההנחיות לשימוש בפלטפורמה. 
              השימוש באתר מהווה הסכמה לתנאים אלו. אם אינך מסכים לתנאים, אנא הימנע משימוש בשירות.
            </p>

            <h2>1. הגדרות</h2>
            <ul>
              <li><strong>"הפלטפורמה"</strong> או <strong>"האתר"</strong> — אתר {APP_NAME}, לרבות כל תת-הדומיינים, האפליקציות והשירותים הנלווים.</li>
              <li><strong>"משתמש"</strong> — כל אדם הגולש באתר או משתמש בשירותיו, בין אם רשום ובין אם לאו.</li>
              <li><strong>"מפרסם"</strong> — משתמש רשום שמפרסם מערכת או פתרון דיגיטלי באתר.</li>
              <li><strong>"מערכת"</strong> — מוצר דיגיטלי, כלי או שירות המפורסם בפלטפורמה.</li>
              <li><strong>"תוכן"</strong> — כל מידע, טקסט, תמונות, ביקורות, דירוגים או חומר אחר המועלה לפלטפורמה.</li>
              <li><strong>"אנחנו"</strong>, <strong>"שלנו"</strong> — {APP_NAME}, מפעילי הפלטפורמה.</li>
            </ul>

            <h2>2. תיאור השירות</h2>
            <p>
              {APP_NAME} היא פלטפורמת מרקטפלייס לגילוי, שיתוף וסקירה של מערכות דיגיטליות מתקדמות.
              הפלטפורמה מאפשרת למשתמשים לגלות פתרונות חדשים, לקרוא ולכתוב חוות דעת, ולמפרסמים להציג את יצירותיהם לקהל הרחב.
            </p>
            <p>
              השירות ניתן "כמות שהוא" (AS IS). אנו שומרים לעצמנו את הזכות לשנות, להשעות או להפסיק 
              כל חלק מהשירות בכל עת, עם או בלי הודעה מוקדמת.
            </p>

            <h2>3. הרשמה וחשבון משתמש</h2>
            <h3>3.1 תנאי הרשמה</h3>
            <ul>
              <li>על המשתמש להיות בן 18 ומעלה.</li>
              <li>יש לספק מידע מדויק ועדכני בעת ההרשמה.</li>
              <li>כל משתמש רשאי להחזיק בחשבון אחד בלבד.</li>
            </ul>

            <h3>3.2 אבטחת החשבון</h3>
            <p>
              המשתמש אחראי לשמירה על סודיות פרטי ההתחברות שלו ולכל פעילות המתבצעת תחת חשבונו. 
              יש להודיע לנו מיד על כל שימוש לא מורשה בחשבון.
            </p>

            <h3>3.3 השעיה וחסימה</h3>
            <p>
              אנו שומרים לעצמנו את הזכות להשעות או לחסום חשבון משתמש, באופן זמני או קבוע, 
              במקרה של הפרת תנאי שימוש אלו, לרבות אך לא רק: פרסום תוכן מטעה, ספאם, או התנהגות פוגענית.
            </p>

            <h2>4. פרסום מערכות</h2>
            <h3>4.1 תהליך אישור</h3>
            <p>
              כל מערכת שמפורסמת בפלטפורמה עוברת תהליך בדיקה ואישור על ידי צוות {APP_NAME}. 
              אנו שומרים לעצמנו את הזכות לאשר, לדחות או להסיר כל מערכת לפי שיקול דעתנו הבלעדי.
            </p>

            <h3>4.2 אחריות המפרסם</h3>
            <ul>
              <li>המפרסם מתחייב כי כל המידע שהוא מספק על המערכת הוא מדויק, עדכני ואינו מטעה.</li>
              <li>המפרסם הוא הבעלים או בעל הרשאה חוקית לפרסם את המערכת.</li>
              <li>המפרסם אחראי לעדכן את פרטי המערכת באופן שוטף.</li>
              <li>אין לפרסם תוכן שמפר זכויות קניין רוחני של צד שלישי.</li>
            </ul>

            <h3>4.3 תמונות ומדיה</h3>
            <p>
              המפרסם מעניק ל-{APP_NAME} רישיון לא בלעדי להציג את התמונות והמדיה שהועלו 
              לצורך הצגת המערכת בפלטפורמה. אין להעלות תמונות שמפרות זכויות יוצרים.
            </p>

            <h2>5. דירוגים וביקורות</h2>
            <h3>5.1 כללי דירוג</h3>
            <ul>
              <li>כל משתמש רשום רשאי לדרג כל מערכת <strong>פעם אחת בלבד</strong>.</li>
              <li>עריכת דירוג אפשרית <strong>פעם ב-24 שעות</strong> בלבד.</li>
              <li>לא ניתן לדרג מערכת שבבעלותך.</li>
              <li>דירוגים חייבים לשקף חוויה אמיתית ואישית עם המערכת.</li>
            </ul>

            <h3>5.2 תוכן אסור בביקורות</h3>
            <ul>
              <li>שפה פוגענית, מאיימת, גזענית או מפלה.</li>
              <li>תוכן שיווקי, ספאם או קישורים פרסומיים.</li>
              <li>מידע אישי של צדדים שלישיים.</li>
              <li>ביקורות מזויפות, בין אם חיוביות (על מערכת שלך) ובין אם שליליות (על מתחרים).</li>
            </ul>

            <h3>5.3 הסרת ביקורות</h3>
            <p>
              אנו שומרים לעצמנו את הזכות להסיר ביקורות שמפרות תנאים אלו, ללא הודעה מוקדמת. 
              משתמש שמפר שוב ושוב את כללי הביקורות עלול להיחסם מהפלטפורמה.
            </p>

            <h2>6. קניין רוחני</h2>
            <p>
              כל הזכויות בפלטפורמה, לרבות העיצוב, הקוד, הלוגו, הטקסטים והמבנה, שייכות ל-{APP_NAME} 
              ומוגנות על ידי חוקי קניין רוחני. אין להעתיק, לשכפל, להפיץ או לעשות שימוש מסחרי 
              בכל חלק מהפלטפורמה ללא אישור מפורש בכתב.
            </p>
            <p>
              תוכן שמועלה על ידי משתמשים (ביקורות, תמונות) נשאר בבעלות המשתמש, 
              אך המשתמש מעניק ל-{APP_NAME} רישיון להציגו בפלטפורמה.
            </p>

            <h2>7. פרטיות ומידע אישי</h2>
            <p>
              השימוש בפלטפורמה כפוף גם ל<Link to="/privacy">מדיניות הפרטיות</Link> שלנו. 
              אנו מתחייבים לשמור על פרטיות המשתמשים בהתאם לחוק הגנת הפרטיות, התשמ"א-1981.
            </p>

            <h2>8. הגבלת אחריות</h2>
            <p>
              {APP_NAME} משמשת כפלטפורמת מידע בלבד ואינה צד להתקשרות בין משתמשים למפרסמים.
            </p>
            <ul>
              <li>אנו לא אחראים לדיוק, לשלמות או לעדכניות של המידע שמפורסם על ידי מפרסמים.</li>
              <li>אנו לא אחראים לכל נזק ישיר או עקיף הנובע מהסתמכות על מידע בפלטפורמה.</li>
              <li>אנו לא ערבים לאיכות, לאמינות או לתפקוד של מערכות המפורסמות באתר.</li>
              <li>ההחלטה להשתמש במערכת כלשהי היא באחריות המשתמש בלבד.</li>
            </ul>

            <h2>9. שימוש אסור</h2>
            <p>המשתמש מתחייב שלא:</p>
            <ul>
              <li>להשתמש בפלטפורמה לכל מטרה בלתי חוקית.</li>
              <li>לנסות לגשת למערכות או לנתונים שלא הורשה לגשת אליהם.</li>
              <li>להשתמש בבוטים, סקריפטים או כלים אוטומטיים לגרידת נתונים (scraping).</li>
              <li>להטעות משתמשים אחרים לגבי זהותו או שיוכו.</li>
            </ul>

            <h2>10. שינויים בתנאי השימוש</h2>
            <p>
              אנו שומרים לעצמנו את הזכות לעדכן תנאי שימוש אלו בכל עת. שינויים מהותיים יפורסמו 
              באתר ו/או יישלחו בהתראה למשתמשים רשומים. המשך השימוש בפלטפורמה לאחר עדכון 
              התנאים מהווה הסכמה לתנאים המעודכנים.
            </p>

            <h2>11. דין חל וסמכות שיפוט</h2>
            <p>
              תנאי שימוש אלו כפופים לדין הישראלי בלבד. כל מחלוקת הנובעת מהשימוש בפלטפורמה 
              תידון בבתי המשפט המוסמכים במחוז תל אביב-יפו.
            </p>

            <h2>12. יצירת קשר</h2>
            <p>
              לשאלות, הבהרות או דיווח על הפרות של תנאי שימוש אלו, ניתן לפנות אלינו:
            </p>
            <ul>
              <li>דוא"ל: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></li>
            </ul>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Terms;